import { useState, useRef, useEffect, ReactNode, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { MoreVertical, Image, FileSpreadsheet, FileImage, Maximize2, Printer, X } from 'lucide-react'
import { exportToSVG, exportToPNG, exportToCSV } from '../../utils/chartExport'

interface ChartWrapperProps {
  title: string
  children: ReactNode
  /** Optional data for CSV export. Accepts any typed array of chart data objects. */
  data?: object[]
  /** Optional custom filename for exports */
  exportFilename?: string
  /** Optional legend items to display */
  legend?: Array<{
    color: string
    label: string
    /** Optional dataKey for hover-to-highlight (matches Recharts dataKey) */
    dataKey?: string
  }>
  /** Optional series toggle callback */
  onSeriesToggle?: (series: string, visible: boolean) => void
  /** Optional visible series state for toggle buttons */
  visibleSeries?: Record<string, boolean>
  /** CR-3827: Hovered series for highlight effect */
  hoveredSeries?: string | null
  /** CR-3827: Callback when legend item is hovered */
  onSeriesHover?: (series: string | null) => void
  /** Optional click handler for chart drill-down */
  onClick?: () => void
  /** Optional className for the card */
  className?: string
  /** Optional header actions (rendered before the menu) */
  headerActions?: ReactNode
}

export function ChartWrapper({
  title,
  children,
  data,
  exportFilename,
  legend,
  onSeriesToggle,
  visibleSeries,
  hoveredSeries,
  onSeriesHover,
  onClick,
  className = '',
  headerActions,
}: ChartWrapperProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Generate filename from title if not provided
  const filename = exportFilename || title.toLowerCase().replace(/[^a-z0-9]+/g, '-')

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isFullscreen])

  // CR-3829: Toggle fullscreen mode
  const handleFullscreen = useCallback(() => {
    setIsFullscreen(true)
    setMenuOpen(false)
  }, [])

  // CR-3830: Print chart
  const handlePrint = useCallback(() => {
    setMenuOpen(false)

    // Create a print-specific window with just the chart
    const printWindow = window.open('', '_blank', 'width=800,height=600')
    if (!printWindow || !chartRef.current) return

    // Get the SVG or chart content
    const chartContent = chartRef.current.innerHTML

    // Sanitize title to prevent XSS in print window
    const sanitizedTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${sanitizedTitle}</title>
          <style>
            body {
              margin: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }
            h1 {
              font-size: 18px;
              margin-bottom: 20px;
              color: #333;
            }
            .chart-container {
              width: 100%;
              max-width: 800px;
            }
            @media print {
              body { margin: 0; }
              h1 { margin-bottom: 10px; }
            }
          </style>
        </head>
        <body>
          <h1>${sanitizedTitle}</h1>
          <div class="chart-container">${chartContent}</div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }, [title])

  const handleExportSVG = () => {
    exportToSVG(chartRef.current, filename)
    setMenuOpen(false)
  }

  const handleExportPNG = async () => {
    await exportToPNG(chartRef.current, filename)
    setMenuOpen(false)
  }

  const handleExportCSV = () => {
    if (data && data.length > 0) {
      exportToCSV(data as Record<string, unknown>[], filename)
    }
    setMenuOpen(false)
  }

  return (
    <Card className={`shadow-sm ${className}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <div className="flex items-center space-x-2">
            {/* Legend - CR-3827: Added hover-to-highlight functionality */}
            {legend && legend.length > 0 && (
              <div className="flex items-center space-x-3 text-xs mr-2">
                {legend.map((item, idx) => {
                  const seriesKey = item.dataKey || item.label
                  const isHovered = hoveredSeries === seriesKey
                  const hasHover = hoveredSeries !== null && hoveredSeries !== undefined
                  const isHiddenByHover = hasHover && !isHovered

                  return (
                    <button
                      key={idx}
                      className={`flex items-center transition-opacity duration-150 ${
                        visibleSeries && onSeriesToggle
                          ? 'cursor-pointer'
                          : onSeriesHover
                          ? 'cursor-pointer'
                          : 'cursor-default'
                      } ${
                        visibleSeries && visibleSeries[item.label] === false
                          ? 'opacity-40'
                          : isHiddenByHover
                          ? 'opacity-40'
                          : isHovered
                          ? 'opacity-100 font-semibold'
                          : ''
                      }`}
                      onClick={() => {
                        if (onSeriesToggle && visibleSeries !== undefined) {
                          onSeriesToggle(item.label, !(visibleSeries[item.label] ?? true))
                        }
                      }}
                      onMouseEnter={() => {
                        if (onSeriesHover) {
                          onSeriesHover(seriesKey)
                        }
                      }}
                      onMouseLeave={() => {
                        if (onSeriesHover) {
                          onSeriesHover(null)
                        }
                      }}
                      disabled={!onSeriesToggle && !onSeriesHover}
                    >
                      <span
                        className={`w-2 h-2 rounded-full mr-1 transition-transform duration-150 ${isHovered ? 'scale-125' : ''}`}
                        style={{ backgroundColor: item.color }}
                      ></span>
                      {item.label}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Custom header actions */}
            {headerActions}

            {/* Export Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-1 rounded hover:bg-muted transition-colors"
                aria-label="Chart options"
              >
                <MoreVertical className="h-4 w-4 text-muted-foreground" />
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-card border rounded-md shadow-lg z-50 py-1 min-w-[160px]">
                  {/* CR-3829: View in Full Screen */}
                  <button
                    onClick={handleFullscreen}
                    className="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                  >
                    <Maximize2 className="h-4 w-4 mr-2 text-muted-foreground" />
                    View Full Screen
                  </button>
                  {/* CR-3830: Print Chart */}
                  <button
                    onClick={handlePrint}
                    className="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                  >
                    <Printer className="h-4 w-4 mr-2 text-muted-foreground" />
                    Print Chart
                  </button>
                  <div className="border-t my-1" />
                  <button
                    onClick={handleExportSVG}
                    className="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                  >
                    <FileImage className="h-4 w-4 mr-2 text-muted-foreground" />
                    Download SVG
                  </button>
                  <button
                    onClick={handleExportPNG}
                    className="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                  >
                    <Image className="h-4 w-4 mr-2 text-muted-foreground" />
                    Download PNG
                  </button>
                  {data && data.length > 0 && (
                    <button
                      onClick={handleExportCSV}
                      className="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2 text-muted-foreground" />
                      Download CSV
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div
          ref={chartRef}
          onClick={onClick}
          className={onClick ? 'cursor-pointer' : ''}
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
        >
          {children}
        </div>
      </CardContent>

      {/* CR-3829: Fullscreen Overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          {/* Fullscreen Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-card">
            <h2 className="text-lg font-semibold">{title}</h2>
            <div className="flex items-center gap-2">
              {/* Legend in fullscreen - CR-3827: hover-to-highlight */}
              {legend && legend.length > 0 && (
                <div className="flex items-center space-x-3 text-sm mr-4">
                  {legend.map((item, idx) => {
                    const seriesKey = item.dataKey || item.label
                    const isHovered = hoveredSeries === seriesKey
                    const hasHover = hoveredSeries !== null && hoveredSeries !== undefined
                    const isHiddenByHover = hasHover && !isHovered

                    return (
                      <button
                        key={idx}
                        className={`flex items-center transition-opacity duration-150 ${
                          onSeriesHover ? 'cursor-pointer' : 'cursor-default'
                        } ${
                          isHiddenByHover
                            ? 'opacity-40'
                            : isHovered
                            ? 'opacity-100 font-semibold'
                            : ''
                        }`}
                        onMouseEnter={() => onSeriesHover?.(seriesKey)}
                        onMouseLeave={() => onSeriesHover?.(null)}
                      >
                        <span
                          className={`w-3 h-3 rounded-full mr-1.5 transition-transform duration-150 ${isHovered ? 'scale-125' : ''}`}
                          style={{ backgroundColor: item.color }}
                        ></span>
                        {item.label}
                      </button>
                    )
                  })}
                </div>
              )}
              <button
                onClick={handlePrint}
                className="p-2 rounded hover:bg-muted transition-colors"
                aria-label="Print chart"
              >
                <Printer className="h-5 w-5 text-muted-foreground" />
              </button>
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 rounded hover:bg-muted transition-colors"
                aria-label="Exit full screen"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
          </div>
          {/* Fullscreen Chart Content */}
          <div className="flex-1 p-6 overflow-auto">
            <div className="w-full h-full min-h-[500px]">
              {children}
            </div>
          </div>
          {/* Fullscreen Footer */}
          <div className="px-6 py-3 border-t bg-card text-sm text-muted-foreground text-center">
            Press ESC or click X to exit full screen
          </div>
        </div>
      )}
    </Card>
  )
}

export default ChartWrapper
